import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";

const SubGroup = () => {
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [dataLimit, setDataLimit] = useState(10);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // Send to Device States
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [showJsonPackageModal, setShowJsonPackageModal] = useState(false);
  const [currentRowItem, setCurrentRowItem] = useState(null);
  const [jsonInput, setJsonInput] = useState("");
  const [packageJsonInput, setPackageJsonInput] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [jsonPackageActions, setJsonPackageActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [isSendingJson, setIsSendingJson] = useState(false);
  const [isSendingPackage, setIsSendingPackage] = useState(false);
  const [rowActionName, setRowActionName] = useState("Clean");

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token");
  const userid = getCookie("userid");

  // Fetch SubGroup data
  const fetchSubGroupData = async () => {
    setLoading(true);
    try {
      const SubGroupSearch = getUrlByOperation("SubGroupSearch") || 
        "https://api.dqr-display-tms.bonrix.in/api/SubGroupOperation/Search";
      
      const response = await axios.post(
        SubGroupSearch,
        {
          groupid: parseInt(id),
          ispageload: false,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
        setHasMore(response.data.data.length === dataLimit);
      } else {
        setData([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching subgroup data:", error);
      setMessage("Failed to fetch subgroup data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Group Name
  const fetchGroupName = async () => {
    try {
      const GroupSearch = getUrlByOperation("GroupSearch") || 
        "https://api.dqr-display-tms.bonrix.in/api/GroupOperation/Search";
      
      const response = await axios.post(
        GroupSearch,
        {
          id: parseInt(id),
          ispageload: false,
          pagesize: 1,
          currentpageno: 0,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      if (response.data && response.data.data && response.data.data[0]) {
        setGroupName(response.data.data[0].groupname || response.data.data[0].name || `Group #${id}`);
      }
    } catch (error) {
      console.error("Error fetching group name:", error);
      setGroupName(`Group #${id}`);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchGroupName();
      fetchSubGroupData();
    }
  }, [getUrlByOperation, token, userid, dataLimit, count, id]);

  // Pagination navigation functions
  const goFirst = () => {
    if (count !== 0) setCount(0);
  };
  
  const goPrev = () => {
    if (count > 0) setCount(count - 1);
  };
  
  const goNext = () => {
    if (hasMore) setCount(count + 1);
  };

  const handleAddSubGroup = () => {
    navigate(`/addsubgroup/${id}`);
  };

  const handleUpdateSubGroup = (subgroupId) => {
    navigate(`/updatesubgroup/${subgroupId}`);
  };

  const handleDelete = async (subgroupId) => {
    if (window.confirm("Are you sure you want to delete this subgroup?")) {
      try {
        const SubGroupDelete = getUrlByOperation("SubGroupDelete") || 
          "https://api.dqr-display-tms.bonrix.in/api/SubGroupOperation/Delete";
        
        await axios.post(
          SubGroupDelete,
          { id: subgroupId },
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
              accept: "text/plain",
            },
          }
        );
        setMessage("SubGroup deleted successfully!");
        fetchSubGroupData(); // Refresh data after delete
      } catch (error) {
        console.error("Error deleting subgroup:", error);
        setMessage("Failed to delete subgroup");
      }
    }
  };

  const handleToggle = async (item) => {
    try {
      const apiEndpoint = item.active 
        ? "https://api.dqr-display-tms.bonrix.in/api/SubGroupOperation/UpdateStatus/Deactive"
        : "https://api.dqr-display-tms.bonrix.in/api/SubGroupOperation/UpdateStatus/Active";
      
      const SubGroupActive = getUrlByOperation("SubGroupActive");
      const SubGroupDeactive = getUrlByOperation("SubGroupDeactive");
      
      const url = item.active 
        ? (SubGroupDeactive || apiEndpoint.replace("Active", "Deactive"))
        : (SubGroupActive || apiEndpoint);

      await axios.post(
        url,
        { id: item.id },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
      setMessage(item.active ? "SubGroup deactivated successfully!" : "SubGroup activated successfully!");
    } catch (error) {
      console.error("Error updating subgroup status:", error);
      setMessage("Failed to update subgroup status");
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  const handleUploadSVG = (subgroupId) => {
    navigate(`/subgroupdata/${subgroupId}/${id}`);
  };

  const handleSendRowJson = (item) => {
    setCurrentRowItem(item);
    setShowJsonModal(true);
    setJsonInput("");
    setRowActionName("Clean");
  };

  const submitSendRowJson = async () => {
    if (!currentRowItem) return;

    if (!token || !userid) {
      navigate("/login");
      return;
    }

    setIsSendingJson(true);
    const SendToDeviceSGGeneralAll = getUrlByOperation("SendToDeviceSGGeneralAll");
    const apiUrl = SendToDeviceSGGeneralAll || "https://api.dqr-display-tms.bonrix.in/api/SendToDevice/SG/GeneralAll";

    const requestBody = {
      subgroupids: [parseInt(currentRowItem.id)],
      actionname: rowActionName || "Clean",
      withaction: false,
      msg: jsonInput
    };

    try {
      const response = await axios.post(apiUrl, requestBody, {
        headers: {
          "accept": "text/plain",
          "UserId": userid,
          "X-Authorization": token,
          "Content-Type": "application/json-patch+json",
        },
      });

      if (response.data && response.data.isresponse) {
        if (Array.isArray(response.data.sendresult) && response.data.sendresult.length > 0) {
          const failures = response.data.sendresult.filter(
            (r) => r.responsestatus === "FAIL" || r.isresponse === false
          );
          if (failures.length > 0) {
            const errorMsgs = failures.map(
              (r) => `${r.posname || "Device"}: ${r.errormsg || "failed"}`
            );
            setMessage(`Failed: ${errorMsgs.join(", ")}`);
            setShowJsonModal(false);
            return;
          }
        }
        setMessage("JSON sent successfully!");
        setShowJsonModal(false);
      } else {
        setMessage(response.data?.errormsg || "Failed to send JSON");
      }
    } catch (error) {
      console.error("Error sending JSON:", error);
      setMessage("Error sending JSON");
    } finally {
      setIsSendingJson(false);
    }
  };

  const closeJsonModal = () => {
    setShowJsonModal(false);
    setCurrentRowItem(null);
  };

  const handleSendJsonPackage = (item) => {
    setCurrentRowItem(item);
    setShowJsonPackageModal(true);
    setPackageJsonInput('');
    setSelectedAction("");
    fetchJsonPackageActions();
  };

  const fetchJsonPackageActions = async () => {
    if (!token || !userid) {
      navigate("/login");
      return;
    }

    setLoadingActions(true);
    try {
      const getDistinctUrl = getUrlByOperation("ActionOperationGetDistinctName") || 
        "https://api.dqr-display-tms.bonrix.in/api/ActionOperation/GetDistinct/name";
      
      const response = await axios.get(
        getDistinctUrl,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "accept": "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setJsonPackageActions(response.data.data);
      } else {
        setJsonPackageActions([]);
      }
    } catch (error) {
      console.error("Error fetching actions:", error);
      setJsonPackageActions([]);
    } finally {
      setLoadingActions(false);
    }
  };

  const handleActionChange = async (actionName) => {
    setSelectedAction(actionName);
    if (!actionName) {
      setPackageJsonInput("");
      return;
    }

    const selectedObj = jsonPackageActions.find(act => act.value === actionName);
    if (!selectedObj) return;

    setPackageJsonInput("Loading configuration JSON...");

    const getUrl = getUrlByOperation("ActionOperationGet") || 
      "https://api.dqr-display-tms.bonrix.in/api/ActionOperation/Get";

    try {
      const response = await axios.post(
        getUrl,
        {
          id: selectedObj.id,
          name: selectedObj.value
        },
        {
          headers: {
            "accept": "text/plain",
            "UserId": userid,
            "X-Authorization": token,
            "Content-Type": "application/json-patch+json"
          }
        }
      );

      if (response.data && response.data.isresponse && Array.isArray(response.data.data) && response.data.data.length > 0) {
        setPackageJsonInput(response.data.data[0].json || "");
      } else {
        setPackageJsonInput("");
      }
    } catch (error) {
      console.error("Error fetching action JSON:", error);
      setPackageJsonInput("");
    }
  };

  const closeJsonPackageModal = () => {
    setShowJsonPackageModal(false);
    setCurrentRowItem(null);
    setSelectedAction("");
    setPackageJsonInput('');
  };

  const submitSendJsonPackage = async () => {
    if (!currentRowItem || !selectedAction) return;

    if (!token || !userid) {
      navigate("/login");
      return;
    }

    setIsSendingPackage(true);
    const SendToDeviceSGGeneralAll = getUrlByOperation("SendToDeviceSGGeneralAll");
    const apiUrl = SendToDeviceSGGeneralAll || "https://api.dqr-display-tms.bonrix.in/api/SendToDevice/SG/GeneralAll";

    const requestBody = {
      subgroupids: [parseInt(currentRowItem.id)],
      actionname: selectedAction,
      withaction: true,
      msg: packageJsonInput
    };

    try {
      const response = await axios.post(apiUrl, requestBody, {
        headers: {
          "accept": "text/plain",
          "UserId": userid,
          "X-Authorization": token,
          "Content-Type": "application/json-patch+json",
        },
      });

      if (response.data && response.data.isresponse) {
        if (Array.isArray(response.data.sendresult) && response.data.sendresult.length > 0) {
          const failures = response.data.sendresult.filter(
            (r) => r.responsestatus === "FAIL" || r.isresponse === false
          );
          if (failures.length > 0) {
            const errorMsgs = failures.map(
              (r) => `${r.posname || "Device"}: ${r.errormsg || "failed"}`
            );
            setMessage(`Failed: ${errorMsgs.join(", ")}`);
            closeJsonPackageModal();
            return;
          }
        }
        setMessage("JSON Package sent successfully!");
        closeJsonPackageModal();
      } else {
        setMessage(response.data?.errormsg || "Failed to send JSON Package");
      }
    } catch (error) {
      console.error("Error sending JSON Package:", error);
      setMessage("Error sending JSON Package");
    } finally {
      setIsSendingPackage(false);
    }
  };

  // Clean up message after display
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (pageLoading) {
    return (
      <>
        <Menu />
        <div className="wrapper">
          <div className="content-wrapper">
            <section className="content">
              <div className="container-fluid text-center p-5">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                <div>
                  <h1 className="mb-0">SubGroups</h1>
                  <p className="text-muted mb-0">
                    Group: <strong>{groupName}</strong>
                  </p>
                </div>
                <div>
                  <button 
                    type="button" 
                    className="btn btn-secondary mr-2" 
                    onClick={() => navigate("/group")}
                  >
                    <i className="fas fa-arrow-left mr-1"></i>Back to Groups
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-success" 
                    onClick={handleAddSubGroup}
                  >
                    <i className="fas fa-plus mr-1"></i>Add SubGroup
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="card shadow-sm">
               
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover table-striped mb-0">
                        <thead className="thead-dark">
                          <tr>
                            <th style={{ width: "5%" }} className="text-center">
                              ID
                            </th>
                            <th style={{ width: "20%" }}>SubGroup Name</th>
                            <th style={{ width: "30%" }}>Description</th>
                            <th style={{ width: "25%" }} className="text-center">
                              Actions
                            </th>
                            <th style={{ width: "10%" }} className="text-center">
                              Active
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="5" className="text-center py-4">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                {" "}Loading...
                              </td>
                            </tr>
                          ) : data.length > 0 ? (
                            data.map((item, index) => {
                              const isDefault = item.subgroupname?.toLowerCase() === "default" || item.name?.toLowerCase() === "default";
                              return (
                                <tr key={index}>
                                  <td className="text-center align-middle">{item.id}</td>
                                  <td className="align-middle">
                                    <strong>{item.subgroupname || item.name || "-"}</strong>
                                    {isDefault && (
                                      <span className="badge badge-secondary ml-2" style={{ fontSize: "0.7rem" }}>
                                        DEFAULT
                                      </span>
                                    )}
                                  </td>
                                  <td className="align-middle">{item.description || item.remark || "-"}</td>
                                  <td className="text-center align-middle">
                                    <div className="d-flex justify-content-center flex-wrap gap-1">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-info"
                                        onClick={() => navigate(`/subgroupdatalist/${item.id}/${id}`)}
                                        title="View All Data"
                                      >
                                        <i className="fas fa-list mr-1"></i>View Data
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() => !isDefault && handleUpdateSubGroup(item.id)}
                                        title={isDefault ? "Cannot edit Default SubGroup" : "Edit SubGroup"}
                                        disabled={isDefault}
                                        style={isDefault ? { 
                                          cursor: "not-allowed", 
                                          opacity: 0.4,
                                          filter: "blur(0.5px)"
                                        } : {}}
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-warning"
                                        onClick={() => handleUploadSVG(item.id, item.subgroupname || item.name)}
                                        title="Upload SVG"
                                      >
                                        <i className="fas fa-file-upload"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleSendJsonPackage(item)}
                                        title="Send JSON Package"
                                      >
                                        <i className="fas fa-box"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-warning"
                                        onClick={() => handleSendRowJson(item)}
                                        title="Send Row JSON"
                                      >
                                        <i className="fas fa-paper-plane"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => !isDefault && handleDelete(item.id)}
                                        title={isDefault ? "Cannot delete Default SubGroup" : "Delete SubGroup"}
                                        disabled={isDefault}
                                        style={isDefault ? { 
                                          cursor: "not-allowed", 
                                          opacity: 0.4,
                                          filter: "blur(0.5px)"
                                        } : {}}
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                  <td className="text-center align-middle">
                                    <Switch
                                      isOn={item.active}
                                      handleToggle={() => !isDefault && handleToggle(item)}
                                      id={item.id}
                                      disabled={isDefault}
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center py-4">
                                <i className="fas fa-inbox text-muted" style={{ fontSize: "2rem" }}></i>
                                <p className="text-muted mt-2 mb-0">No subgroups found</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-3 p-3">
                      <div>
                        <button 
                          className="btn btn-outline-secondary btn-sm mr-2" 
                          onClick={goFirst} 
                          disabled={loading || count === 0}
                        >
                          <i className="fas fa-angle-double-left"></i> First
                        </button>
                        <button 
                          className="btn btn-outline-secondary btn-sm" 
                          onClick={goPrev} 
                          disabled={loading || count === 0}
                        >
                          <i className="fas fa-angle-left"></i> Prev
                        </button>
                      </div>
                      <div>
                        <span className="badge badge-light p-2">Page {count + 1}</span>
                      </div>
                      <div>
                        <button 
                          className="btn btn-outline-secondary btn-sm" 
                          onClick={goNext} 
                          disabled={loading || !hasMore}
                        >
                          Next <i className="fas fa-angle-right"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Message display */}
      {message && (
        <div style={{ 
          position: "fixed", 
          top: "20px", 
          right: "20px", 
          width: "350px", 
          zIndex: "9999" 
        }}>
          <div
            className={`alert ${
              message.includes("successfully") ? "alert-success" : "alert-danger"
            } alert-dismissible fade show shadow-sm`}
            role="alert"
          >
            <i className={`fas ${
              message.includes("successfully") ? "fa-check-circle" : "fa-exclamation-circle"
            } mr-2`}></i>
            {message}
          </div>
        </div>
      )}


      {/* Row JSON Modal */}
      {
        showJsonModal && (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Send JSON to SubGroup</h5>
                  <button type="button" className="close" onClick={closeJsonModal}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group mb-3">
                    <label>Action Name:</label>
                    <input
                      type="text"
                      className="form-control"
                      value={rowActionName}
                      onChange={(e) => setRowActionName(e.target.value)}
                      placeholder="Enter action name"
                    />
                  </div>
                  <div className="form-group">
                    <label>JSON Message:</label>
                    <textarea
                      className="form-control"
                      rows="8"
                      value={jsonInput}
                      onChange={(e) => setJsonInput(e.target.value)}
                      style={{ fontFamily: "monospace" }}
                    ></textarea>
                    <small className="text-muted">Enter the valid JSON object to be sent in the 'msg' field.</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeJsonModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={submitSendRowJson}
                    disabled={isSendingJson}
                  >
                    {isSendingJson ? "Sending..." : "Send JSON"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Row JSON Package Modal */}
      {
        showJsonPackageModal && (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Send JSON Package to SubGroup</h5>
                  <button type="button" className="close" onClick={closeJsonPackageModal}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Select Action:</label>
                    {loadingActions ? (
                      <div className="text-center">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <select
                        className="form-control"
                        value={selectedAction}
                        onChange={(e) => handleActionChange(e.target.value)}
                      >
                        <option value="">--Select Action--</option>
                        {jsonPackageActions.map((action, index) => (
                          <option key={index} value={action.value}>
                            {action.value}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="form-group mt-3">
                    <label>JSON Message:</label>
                    <textarea
                      className="form-control"
                      rows="8"
                      value={packageJsonInput}
                      onChange={(e) => setPackageJsonInput(e.target.value)}
                      style={{ fontFamily: "monospace" }}
                    ></textarea>
                    <small className="text-muted">Enter the valid JSON object to be sent in the 'msg' field.</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeJsonPackageModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!selectedAction || isSendingPackage}
                    onClick={submitSendJsonPackage}
                  >
                    {isSendingPackage ? "Sending..." : "Send"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default SubGroup;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}